import React, { useState } from 'react';
import { Alert, Button, Spin, Switch, Typography } from 'antd';
// Hooks
import { useGetCbSettings, useSetCbSetting } from 'hooks';
// Types
import {
  GetCbSettings,
  GetCbSettings_getCbSettings_CbDigitalAgreementSetting,
} from 'api/cbSettings/types/GetCbSettings';
import {
  CampaignType,
  CbSettingScope,
  CbSettingType,
} from 'api/graphql-global-types';
// Helpers
import {
  DIGITAL_AGREEMENT_CAMPAIGN_TYPES,
  getCampaignTypeLabel,
} from 'helpers/campaign';
// UI
import { successNotification, errorNotification } from 'ui/Notification';
// Styles
import styles from './DigitalAgreementSettings.module.scss';

const { Text } = Typography;

type DigitalAgreementSetting =
  GetCbSettings_getCbSettings_CbDigitalAgreementSetting;

const isDigitalAgreementSetting = (
  setting: GetCbSettings['getCbSettings'][number]
): setting is DigitalAgreementSetting =>
  setting.__typename === 'CbDigitalAgreementSetting';

/**
 * One switch per campaign type. Like Fee and ProcessingFee, the backend falls back to a global row (no campaign type)
 * for a type without its own; it isn't shown, but the switch reflects it. Saving gives the type its own row.
 */
const DigitalAgreementSettings = (): JSX.Element => {
  // Unsaved switch positions, by campaign type
  const [draft, setDraft] = useState<Partial<Record<CampaignType, boolean>>>(
    {}
  );
  const [saving, setSaving] = useState(false);

  const { data, loading, error, refetch } = useGetCbSettings({
    scope: CbSettingScope.Global,
    type: CbSettingType.DigitalAgreement,
  });
  const [setCbSetting] = useSetCbSetting();

  const settings = (data?.getCbSettings ?? []).filter(
    isDigitalAgreementSetting
  );
  const globalEnabled = settings.find(
    (setting) => !setting.campaignType
  )?.enabled;
  const getSavedValue = (campaignType: CampaignType): boolean =>
    settings.find((setting) => setting.campaignType === campaignType)
      ?.enabled ??
    globalEnabled ??
    false;
  const getValue = (campaignType: CampaignType): boolean =>
    draft[campaignType] ?? getSavedValue(campaignType);

  const changedTypes = DIGITAL_AGREEMENT_CAMPAIGN_TYPES.filter(
    (campaignType) =>
      draft[campaignType] !== undefined &&
      draft[campaignType] !== getSavedValue(campaignType)
  );

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    const saved: CampaignType[] = [];
    try {
      for (const campaignType of changedTypes) {
        await setCbSetting({
          variables: {
            input: {
              type: CbSettingType.DigitalAgreement,
              digitalAgreement: {
                campaignType,
                enabled: getValue(campaignType),
              },
            },
          },
        });
        saved.push(campaignType);
      }
      successNotification('Digital agreement settings saved');
      setDraft({});
    } catch (err: any) {
      errorNotification(
        err?.message || 'Failed to save the digital agreement settings'
      );
      // Keep only what didn't save, so the admin can retry it
      setDraft((current) => {
        const next = { ...current };
        saved.forEach((campaignType) => delete next[campaignType]);
        return next;
      });
    } finally {
      await refetch();
      setSaving(false);
    }
  };

  if (loading && !data) {
    return (
      <div className={styles.loading}>
        <Spin />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Failed to load the digital agreement settings"
        description={error.message}
      />
    );
  }

  return (
    <div className={styles.root}>
      <Text type="secondary">
        When on, talents must sign the campaign&apos;s digital agreement before
        they can apply, and a brand user countersigns it.
      </Text>
      <Alert
        className={styles.notice}
        type="info"
        showIcon
        message="Only campaigns created after a change are affected. Existing campaigns keep the setting they were created with."
      />

      {DIGITAL_AGREEMENT_CAMPAIGN_TYPES.map((campaignType) => (
        <div className={styles.row} key={campaignType}>
          <Text strong>{getCampaignTypeLabel(campaignType)}</Text>
          <Switch
            checked={getValue(campaignType)}
            disabled={saving}
            onChange={(checked) =>
              setDraft((current) => ({ ...current, [campaignType]: checked }))
            }
          />
        </div>
      ))}

      <div className={styles.actions}>
        <Button
          type="primary"
          loading={saving}
          disabled={!changedTypes.length}
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default DigitalAgreementSettings;
